import { createFileRoute } from "@tanstack/react-router";
import {
  addMonths,
  addWeeks,
  endOfWeek,
  format,
  isBefore,
  parseISO,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { CalendarPlus, Lock } from "lucide-react";
import { useState } from "react";
import { EmptyState } from "@/components/brand/empty-state";
import { PageHeader } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Calendar,
  CalendarToolbar,
  type CalendarView,
  DayModal,
  Legend,
  monthKey,
  PlanSetup,
  useCreatePlan,
  usePlanForMonth,
} from "@/features/plans";
import { useIsMobile } from "@/hooks/use-mobile";

/**
 * The SMM's month.
 *
 * Setting up the theme and looking at the calendar are the same page because
 * they are the same job: decide what this month is about, then place it on
 * days. Splitting them would mean bouncing between two screens to do one thing.
 */
export const Route = createFileRoute("/_authenticated/org/plan")({
  component: MonthlyPlanPage,
});

function MonthlyPlanPage() {
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  // A month of seven columns does not fit a phone; the week does.
  const isMobile = useIsMobile();
  const [view, setView] = useState<CalendarView | null>(null);
  const activeView: CalendarView = view ?? (isMobile ? "week" : "month");
  const [weekAnchor, setWeekAnchor] = useState(() => new Date());
  const [openDay, setOpenDay] = useState<Date | null>(null);

  const month = monthKey(cursor);
  const { data: plan, isPending } = usePlanForMonth(month);
  // The server decides this for a plan that exists; for an empty month the page
  // works it out the same way, so a finished month never offers to be started.
  const closed = isBefore(cursor, startOfMonth(new Date()));

  const label =
    activeView === "month"
      ? format(cursor, "MMMM yyyy")
      : `${format(startOfWeek(weekAnchor, { weekStartsOn: 1 }), "d MMM")} to ${format(
          endOfWeek(weekAnchor, { weekStartsOn: 1 }),
          "d MMM yyyy",
        )}`;

  const step = (direction: 1 | -1) => {
    if (activeView === "month") {
      const next = addMonths(cursor, direction);
      setCursor(next);
      setWeekAnchor(next);
      return;
    }
    const next = addWeeks(weekAnchor, direction);
    setWeekAnchor(next);
    setCursor(startOfMonth(next));
  };

  return (
    <>
      <PageHeader
        eyebrow="Your team"
        title="Monthly plan"
        description="Set the month's theme, pin the dates that matter, then place posts on the calendar."
        actions={
          plan ? (
            <span className="flex items-center gap-2">
              {plan.editable ? null : (
                <Badge variant="secondary">
                  <Lock /> Read only
                </Badge>
              )}
              <Badge variant={plan.status === "published" ? "success" : "default"}>
                {plan.status === "published" ? "Published" : "Draft"}
              </Badge>
            </span>
          ) : null
        }
      />

      {isPending ? (
        <Skeleton className="h-96 rounded-lg" />
      ) : !plan ? (
        <StartMonth
          month={month}
          label={format(cursor, "MMMM yyyy")}
          closed={closed}
          onStepMonth={step}
        />
      ) : (
        <div className="space-y-8">
          {/* The calendar is the workspace, so it leads: full width and tall. */}
          <section className="space-y-3">
            <CalendarToolbar
              view={activeView}
              onViewChange={setView}
              label={label}
              onPrevious={() => step(-1)}
              onNext={() => step(1)}
              onToday={() => {
                setCursor(startOfMonth(new Date()));
                setWeekAnchor(new Date());
              }}
            />
            <Calendar
              month={parseISO(plan.month)}
              anchor={weekAnchor}
              view={activeView}
              events={plan.events}
              posts={plan.posts}
              onSelectDay={setOpenDay}
              editable={plan.editable}
            />
            <Legend editable={plan.editable} />
          </section>

          {/* The month's setup lives below the calendar as its controls. */}
          <section>
            <h2 className="mb-3 font-semibold text-foreground text-sm uppercase tracking-[0.08em]">
              Month setup
            </h2>
            <PlanSetup plan={plan} />
          </section>
        </div>
      )}

      {plan ? <DayModal plan={plan} day={openDay} onClose={() => setOpenDay(null)} /> : null}
    </>
  );
}

function StartMonth({
  month,
  label,
  closed,
  onStepMonth,
}: {
  month: string;
  label: string;
  /** A month that has already ended: it can be looked at, never filled in. */
  closed: boolean;
  onStepMonth: (direction: 1 | -1) => void;
}) {
  const create = useCreatePlan();
  const [title, setTitle] = useState("");

  return (
    <div className="mx-auto max-w-xl">
      <div className="mb-4 flex items-center justify-center gap-2">
        <Button variant="outline" size="sm" onClick={() => onStepMonth(-1)}>
          Back
        </Button>
        <p className="min-w-40 text-center font-semibold text-foreground">{label}</p>
        <Button variant="outline" size="sm" onClick={() => onStepMonth(1)}>
          Next
        </Button>
      </div>

      <EmptyState
        title={closed ? `${label} was never planned` : `${label} has not been planned yet`}
        description={
          closed
            ? "That month has finished, so nothing can be added to it now."
            : "Give the month a theme and the calendar opens up. You can change it at any time."
        }
        action={
          closed ? undefined : (
            <div className="w-full max-w-sm space-y-3 text-left">
              <div className="space-y-1.5">
                <Label htmlFor="new-plan-title">Theme for {label}</Label>
                <Input
                  id="new-plan-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Build in public"
                />
              </div>
              <Button
                className="w-full"
                disabled={!title.trim() || create.isPending}
                onClick={() => create.mutate({ body: { month, title: title.trim() } })}
              >
                <CalendarPlus /> Start this month
              </Button>
            </div>
          )
        }
      />
    </div>
  );
}
